import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { CheckCircle2, XCircle, X } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useSlackIntegrationApi, useSlackChannels } from './config';
import SlackIntegrationCard from './components/SlackIntegrationCard';

const Container = styled.div``;

const CardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const CallbackBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 18px;
  max-width: 520px;
  font-size: 13px;
  ${({ $tone }) => ($tone === 'success' ? `
    background: #DCFCE7;
    color: #16A34A;
  ` : `
    background: #FEF2F2;
    color: #E53935;
  `)}
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BannerCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  display: flex;
  align-items: center;
  padding: 0;
`;

const KNOWN_ERROR_REASONS = ['stateInvalid', 'oauthFailed'];

// Slack Bağlayıcısı Task 4 (spec §4) — Entegrasyonlar sekmesi: kart-grid
// yerleşimi (v1'de yalnız Slack kartı, gelecekte Teams/takvim kartları AYNI
// grid'e eklenir — SlackIntegrationCard.js yorumu). OAuth callback dönüşü
// (BE'nin `SlackIntegrationDataAccess.Redirect`'i, t1-report.md §2):
// `?slack=connected` VEYA `?slack=error&reason=stateInvalid|oauthFailed` —
// toast-SUZ inline banner (CLAUDE.md "toast yalnız interceptor" ilkesi; bu
// banner interceptor'ın toast'ı DEĞİL — callback JSON-suz bir 302 redirect,
// hiçbir axios çağrısı hata FIRLATMADI, gösterilecek bilgi yalnız URL'in
// query string'inde). Okunduktan SONRA query string TEMİZLENİR
// (history.replace) — aksi halde sayfa yenilenince banner SESSİZCE yeniden
// belirirdi.
//
// **KRİTİK — redirect-yolu kararı (rapor §"redirect-yolu"):** BE HER ZAMAN
// `/settings/integrations?slack=...`'a döner (DataAccess'te sabit string,
// DEĞİŞTİRİLMEDİ — görev talimatı). Bu sekme `/performance/settings`
// (PerformanceSettingsPage'in mevcut rotası) İÇİNDE yaşıyor — BE'nin
// döndüğü path'i GERÇEK bir sayfa yapmak için `routes/index.js`'e AYRI bir
// `/settings/integrations` girişi eklendi (AYNI `PerformanceSettingsPage`
// bileşenini render eder). `PerformanceSettingsPage`, ya path
// `/settings/integrations` İSE ya da query'de `slack=` VARSA başlangıç
// sekmesini otomatik `integrations`'a ayarlar (bkz. o dosyanın yorumu) — bu
// bileşenin KENDİSİ hangi path'ten geldiğine BAKMAZ, yalnız `location.search`
// okur (iki giriş yolu da AYNI davranışı üretir, savunmacı).
export default function IntegrationsTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('PM');
  const location = useLocation();
  const history = useHistory();

  const [banner, setBanner] = useState(null);

  // Yalnız İLK mount'ta okunur (query string temizlendikten sonra tekrar
  // tetiklenmemeli — bağımlılık dizisi bilinçli boş, bkz. alt yorum).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slackParam = params.get('slack');
    if (!slackParam) return undefined;

    if (slackParam === 'connected') {
      setBanner({ tone: 'success', text: t('integrations.callback.connected') });
    } else if (slackParam === 'error') {
      const reason = params.get('reason');
      const key = KNOWN_ERROR_REASONS.includes(reason)
        ? `integrations.callback.error.${reason}`
        : 'integrations.callback.error.generic';
      setBanner({ tone: 'error', text: t(key) });
    }

    history.replace({ pathname: location.pathname, search: '' });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    status, fetching, getInstallUrl, disconnect, setRecognitionChannel, testConnection,
  } = useSlackIntegrationApi();
  const {
    channels, fetching: channelsFetching, error: channelsError, refresh: refreshChannels,
  } = useSlackChannels(!!status?.connected);

  const handleConnect = useCallback(async () => {
    const url = await getInstallUrl();
    if (url) window.location.href = url;
  }, [getInstallUrl]);

  const handleDisconnect = useCallback(async () => {
    await disconnect();
  }, [disconnect]);

  return (
    <Container>
      {banner && (
        <CallbackBanner data-role="slack-callback-banner" $tone={banner.tone}>
          <BannerLeft>
            {banner.tone === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            <span>{banner.text}</span>
          </BannerLeft>
          <BannerCloseButton type="button" onClick={() => setBanner(null)} aria-label={t('common.close')}>
            <X size={16} />
          </BannerCloseButton>
        </CallbackBanner>
      )}

      <CardGrid>
        <SlackIntegrationCard
          status={status}
          fetching={fetching}
          canManage={canManage}
          channels={channels}
          channelsFetching={channelsFetching}
          channelsError={channelsError}
          onRefreshChannels={refreshChannels}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          onSetRecognitionChannel={setRecognitionChannel}
          onTest={testConnection}
        />
      </CardGrid>
    </Container>
  );
}
